import { useQueryClient } from '@tanstack/react-query';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useActivateVoucher, useGetMyVouchers } from '@/api/gen/vouchers/vouchers.gen';
import { CouponFillIcon } from '@/icons';
import { UseSubscribeUserPlanKey } from '@/lib/auth';
import { showVoucherAppliedToast } from '@/shared/plan/voucherAppliedToast';
import { alertDialog, Button, DialogBody, DialogFooter, DialogTitle, ResponsiveDialog } from '@/ui_v3';
export const PhoneVerificationRewardModal = ({
  onDismiss,
  onNavigate,
  voucherHashId
}: {
  onDismiss: () => Promise<void>;
  onNavigate: () => void;
  voucherHashId?: string;
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    t
  } = useTranslation(['common']);
  const {
    mutateAsync: activateVoucher,
    isPending
  } = useActivateVoucher();

  // PRD 솔루션 2 상태별 처리 — 이미 플랜을 쓰고 있거나 적용 예정이면 지금 쓸 수 없다.
  // 그때는 보관함으로 안내만 하고(시안 28913:51582) 활성화 버튼을 내주지 않는다.
  // 서버가 계정 단위로 판정한 canUseVouchers 를 그대로 따른다.
  // 이 모달은 OverlayProvider 아래, 페이지 바깥 GlobalErrorBoundary 안에서 열린다.
  // 여기서 throw 하면 보상 모달 하나 때문에 앱 전체가 에러 화면이 된다 — 이용권을
  // 받은 직후에 그러면 가장 나쁘다. 실패는 canUseNow=false 로 흡수해 「이용권 보관함」
  // 안내만 남긴다(활성화 버튼을 내주지 않는 쪽이 안전한 기본값이다).
  const {
    data: vouchersResponse
  } = useGetMyVouchers({
    usageStatus: 'UNUSED'
  }, {
    query: {
      throwOnError: false
    }
  });
  const canUseNow = vouchersResponse?.data.canUseVouchers ?? false;
  const from = router.query.from as 'signup' | 'profile_edit' | 'banner_modal' || undefined;

  // 라우팅하지 않는다. 이 모달은 이미 복귀한 화면 위에서 열리므로(PRD 솔루션 1·2)
  // 여기서 또 이동하면 사용자를 원래 작업에서 떼어놓게 된다.
  const handleUseLater = () => {
    logger.track('voucher_storage_click', {
      location: 'phone_verification',
      from
    });
    void onDismiss();
  };
  const handleGoToStorage = () => {
    logger.track('voucher_storage_click', {
      location: 'phone_verification',
      from
    });
    onNavigate();
    void router.push('/profile/plan');
  };
  const handleUseNow = async () => {
    if (!voucherHashId) {
      handleUseLater();
      return;
    }
    try {
      await activateVoucher({
        voucherHashId
      });
      await queryClient.refetchQueries({
        queryKey: UseSubscribeUserPlanKey
      });
      await onDismiss();
      // 헤더에서 쓸 때와 같은 토스트다(시안 28913:51588 · 51593). 적용 사실만 알리고
      // 상세는 플랜 관리로 넘긴다 — 이 경로만 조용하면 같은 행동의 결과가 달라 보인다.
      // URL 신호를 정리한 뒤 토스트를 띄운다. 정리 라우팅의 전역 오버레이 정리가
      // 토스트를 닫지 않도록 순서를 보장한다.
      showVoucherAppliedToast(() => void router.push('/profile/plan'), {
        title: t('common:voucher.applied.title'),
        description: t('common:voucher.applied.description'),
        actionLabel: t('common:planManage')
      });
    } catch (error) {
      logger.captureError(error, {
        tags: {
          feature: 'phone-verification',
          action: 'activateVoucher'
        }
      });
      await alertDialog.openAsync({
        header: {
          title: t('common:voucher.activateFailed.title')
        },
        body: t('common:voucher.commonError.body')
      });
    }
  };
  return <ResponsiveDialog onClose={() => void onDismiss()} size="lg" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="PhoneVerificationRewardModal" data-sentry-source-file="PhoneVerificationRewardModal.tsx">
      <DialogBody data-sentry-element="DialogBody" data-sentry-source-file="PhoneVerificationRewardModal.tsx">
        <main className="flex h-full flex-col gap-md overflow-y-auto p-lg px-md mobile-lg:flex-row mobile-lg:px-lg">
          <div className="flex flex-col gap-sm px-[6px] mobile-lg:gap-xs mobile-lg:p-md mobile-lg:pr-0">
            <div className="flex items-center gap-sm mobile-lg:flex-col mobile-lg:items-start mobile-lg:gap-lg">
              <div className="flex size-[42px] items-center justify-center rounded-sm bg-gradient-to-r from-violet-400 to-blue-400">
                <CouponFillIcon size="lg" fill={ICON_COLOR.inverse} data-sentry-element="CouponFillIcon" data-sentry-source-file="PhoneVerificationRewardModal.tsx" />
              </div>
              {/*
                Radix 는 DialogContent 마다 DialogTitle 을 요구한다(없으면 스크린리더가
                이 창이 무엇인지 읽어 줄 수 없고 콘솔 경고가 난다).
                이미 보이는 제목이 있으므로 sr-only 제목을 따로 두지 않고 asChild 로
                이 h1 자체를 제목으로 지정한다 — 문구가 두 벌이 되면 갈라진다.
               */}
              <DialogTitle asChild data-sentry-element="DialogTitle" data-sentry-source-file="PhoneVerificationRewardModal.tsx">
                <h1 className="text-heading-xl-extrabold">
                  <Trans t={t} i18nKey="phoneVerify.rewardModal.title" components={{
                  br: <>
                          <span className="mobile-lg:hidden">&nbsp;</span>
                          <br className="hidden mobile-lg:block" />
                        </>
                }} data-sentry-element="Trans" data-sentry-source-file="PhoneVerificationRewardModal.tsx" />
                </h1>
              </DialogTitle>
            </div>
            <p className="text-body-lg-medium">
              {t('phoneVerify.rewardModal.description1')}
            </p>
            <p className="text-body-lg-medium">
              {t('phoneVerify.rewardModal.description2')}
            </p>
          </div>
          <div className="min-h-[375px] w-full shrink-0 mobile-lg:min-h-[500px] mobile-lg:max-w-[400px]">
            <img src="/assets/phone-verification-complete.webp" alt="Verification Reward Modal" className="size-full rounded-lg object-cover" />
          </div>
        </main>
      </DialogBody>

      {/* Footer */}
      <DialogFooter className="flex gap-xs border-t border-alternative shadow-lg mobile-lg:pt-md" data-sentry-element="DialogFooter" data-sentry-source-file="PhoneVerificationRewardModal.tsx">
        {canUseNow ? <>
            <Button variant="contained" intent="neutral" size="lg" onClick={handleUseLater} disabled={isPending} className="flex-1">
              {t('common:voucher.useLater')}
            </Button>
            {/*
              시안(28913:51480)은 두 버튼을 1:1 로 나누지 않는다 — 「바로 사용하기」가
              **400px 고정**이고 「다음에」가 남는 폭을 가져간다(720px 기준 264:400).
              주 액션의 크기를 화면 폭에 맡기지 않고 못 박아 둔 것이라, flex-1 로 두면
              둘이 332:332 로 같아져 무엇이 주 액션인지 흐려진다.
               고정폭은 다이얼로그가 실제로 720px 인 mobile-lg 이상에서만 건다
              (`ResponsiveDialog` lg = `w-full mobile-lg:w-[720px]`). 그 아래는 전폭
              드로어라 1:1 을 유지한다. grow 0 / shrink 1 이라 폭이 모자라면 줄어든다.
             */}
            <Button variant="contained" intent="gradient" size="lg" onClick={() => void handleUseNow()} disabled={isPending} className="flex-1 mobile-lg:flex-[0_1_400px]">
              {t('common:voucher.activateNow')}
            </Button>
          </> :
      // 지금 쓸 수 없으니 「다음에」가 성립하지 않는다. 갈 곳 하나만 남긴다.
      <Button variant="contained" intent="gradient" size="lg" rightSlot={<ChevronRightIcon size="md" />} onClick={handleGoToStorage} className="flex-1">
            {t('common:voucher.storage.title')}
          </Button>}
      </DialogFooter>
    </ResponsiveDialog>;
};